/**
 * lib/reports/llmClient.ts — Groq only (then template in narrateSection)
 * ====================================================================================
 * Primary: smallest/cheapest model (fewest tokens for this workload).
 * One backup model if primary fails. Then narrateSection uses template text.
 *
 * SECURITY NOTE: API key is in the client bundle — fine for internal pilot.
 * Cloud Functions: set GROQ_API_KEY secret; process.env is checked first.
 */

type ViteEnv = { env?: Record<string, string | undefined> };

function resolveEnv(...keys: string[]): string | undefined {
  for (const key of keys) {
    const fromProcess =
      typeof process !== 'undefined' ? process.env[key]?.trim() : undefined;
    if (fromProcess) return fromProcess;
  }
  const viteEnv = (import.meta as ImportMeta & ViteEnv).env;
  for (const key of keys) {
    const fromVite = viteEnv?.[key]?.trim();
    if (fromVite) return fromVite;
  }
  return undefined;
}

const PRIMARY_MODEL =
  resolveEnv('GROQ_MODEL', 'VITE_GROQ_MODEL') || 'openai/gpt-oss-20b';

const BACKUP_MODEL =
  resolveEnv('GROQ_BACKUP_MODEL', 'VITE_GROQ_BACKUP_MODEL') || 'qwen/qwen3.6-27b';

async function callGroq(
  model: string,
  systemPrompt: string,
  userPrompt: string,
): Promise<string> {
  const apiKey = resolveEnv('GROQ_API_KEY', 'VITE_GROQ_API_KEY');
  if (!apiKey) throw new Error('GROQ_API_KEY / VITE_GROQ_API_KEY not set');

  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.2,
      max_tokens: 400,
    }),
  });
  if (!res.ok) throw new Error(`Groq ${model} HTTP ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const text = data?.choices?.[0]?.message?.content?.trim();
  if (!text) throw new Error(`Groq ${model} returned no content`);
  return text;
}

/** Primary → one backup → throw (caller uses template). */
export async function narrateWithFallback(
  systemPrompt: string,
  userPrompt: string,
): Promise<{ text: string; provider: 'groq' }> {
  try {
    const text = await callGroq(PRIMARY_MODEL, systemPrompt, userPrompt);
    return { text, provider: 'groq' };
  } catch (primaryErr) {
    console.warn(
      `[weeklyReport] groq primary "${PRIMARY_MODEL}" failed, trying backup "${BACKUP_MODEL}":`,
      (primaryErr as Error).message,
    );
    try {
      const text = await callGroq(BACKUP_MODEL, systemPrompt, userPrompt);
      return { text, provider: 'groq' };
    } catch (backupErr) {
      console.warn(
        `[weeklyReport] groq backup "${BACKUP_MODEL}" failed:`,
        (backupErr as Error).message,
      );
      throw backupErr;
    }
  }
}
